import * as React from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { CalculatePerDiemMutation } from 'generated/resources/graphql'

import {
  Destination,
  PerDiemWizardData,
  createEmptyDestination,
  createInitialWizardData,
} from './types'

type PerDiemResult = NonNullable<
  NonNullable<CalculatePerDiemMutation['expensesMutations']>['calculateExpensesPerDiem']
>['perDiem']

export type PerDiemDay = NonNullable<NonNullable<PerDiemResult>['days']>[number]

export interface CalculationResult {
  totalAmountCents: number
  currency: string
  isCalculating: boolean
  error?: string
  days?: Array<PerDiemDay | null> | null
}

interface PerDiemWizardContextValue {
  data: PerDiemWizardData
  calculation: CalculationResult
  updateData: (updates: Partial<PerDiemWizardData>) => void
  addDestination: () => void
  updateDestination: (id: string, updates: Partial<Destination>) => void
  removeDestination: (id: string) => void
  setCalculation: (result: Partial<CalculationResult>) => void
}

const PerDiemWizardContext = createContext<PerDiemWizardContextValue | null>(null)

interface PerDiemWizardProviderProps {
  children: React.ReactNode
}

const initialCalculation: CalculationResult = {
  totalAmountCents: 0,
  currency: 'EUR',
  isCalculating: false,
}

export const PerDiemWizardProvider = ({ children }: PerDiemWizardProviderProps) => {
  const [data, setData] = useState<PerDiemWizardData>(createInitialWizardData())
  const [calculation, setCalculationState] = useState<CalculationResult>(initialCalculation)

  const updateData = useCallback((updates: Partial<PerDiemWizardData>) => {
    setData((prev) => ({ ...prev, ...updates }))
  }, [])

  const addDestination = useCallback(() => {
    setData((prev) => ({
      ...prev,
      destinations: [...prev.destinations, createEmptyDestination()],
    }))
  }, [])

  const updateDestination = useCallback((id: string, updates: Partial<Destination>) => {
    setData((prev) => ({
      ...prev,
      destinations: prev.destinations.map((dest) =>
        dest.id === id ? { ...dest, ...updates } : dest
      ),
    }))
  }, [])

  const removeDestination = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      destinations: prev.destinations.filter((dest) => dest.id !== id),
    }))
  }, [])

  const setCalculation = useCallback((result: Partial<CalculationResult>) => {
    setCalculationState((prev) => ({ ...prev, ...result }))
  }, [])

  const value = useMemo(
    (): PerDiemWizardContextValue => ({
      data,
      calculation,
      updateData,
      addDestination,
      updateDestination,
      removeDestination,
      setCalculation,
    }),
    [
      data,
      calculation,
      updateData,
      addDestination,
      updateDestination,
      removeDestination,
      setCalculation,
    ]
  )

  return <PerDiemWizardContext.Provider value={value}>{children}</PerDiemWizardContext.Provider>
}

export const usePerDiemWizard = (): PerDiemWizardContextValue => {
  const context = useContext(PerDiemWizardContext)
  if (!context) {
    throw new Error('usePerDiemWizard must be used within a PerDiemWizardProvider')
  }
  return context
}
